<template>
    <div class="shop-wrapper">
        <ShopWidgetSkeleton v-if="loading" :flat="flat" />
        <template v-else>
            <template v-if="shopLightNotOrderable">
                <div class="not-orderable">{{ t("shop.not orderable") }}</div>
            </template>
            <template v-else>
                <ShopWidgetStatus
                    v-show="!hideFresenius"
                    :info-item="infoItem"
                    :more-selected-than-available="moreSelectedThanAvailable"
                    :quantity-selected="quantitySelected"
                />
                <div ref="shopWidgetRef" :class="{ 'shop-widget': true, 'widget-flat': flat }">
                    <ShopWidgetPriceAvailability
                        v-show="!hideFresenius"
                        :flat="flat"
                        :info-item="infoItem"
                        class="col-auto"
                    />

                    <ShopWidgetControls
                        :add-to-basket-error="addToBasketError"
                        :more-selected-than-available="moreSelectedThanAvailable"
                        :price-request-error="priceRequestError"
                        :info-item="infoItem"
                        :quantity-selected="quantitySelected"
                        @quantity-change="quantityInputChange"
                        @add-to-basket="addArticleToBasket"
                        @request-price="sendPriceRequest"
                        class="col"
                        :class="{ 'controls-flat': flat }"
                    />
                </div>
            </template>
        </template>
    </div>
</template>
<script lang="ts" setup>
import { useShopConnector } from "@/abilities/shop/features/shop-core/composables/shop-connector";
import { useShopEvents } from "@/abilities/shop/features/shop-core/composables/use-shop-events";
import { ShopCore } from "@/abilities/shop/types/shop-core";
import { AccessFeature } from "@/shared/access-control/access-control";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access";
import { WidgetContext } from "@/shared/environment/widgets/widget-context";
import { useScreenSize } from "@/shared/screen/composables/screen-size";
import { get, set } from "@vueuse/core";
import { useQuasar } from "quasar";
import { computed, Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useShopStatusStore } from "../../store/shop-status.store";
import ShopWidgetControls from "./shop-widget-controls.vue";
import ShopWidgetPriceAvailability from "./shop-widget-price-availability.vue";
import ShopWidgetSkeleton from "./shop-widget-skeleton.vue";
import ShopWidgetStatus from "./shop-widget-status.vue";

// Props
const props = defineProps<{
    widgetContext: WidgetContext;
}>();

// external logic / functions
const { notify } = useQuasar();
const { isLargerThanMedium } = useScreenSize();
const { t } = useI18n();
const store = useShopStatusStore();
const { sendPriceRequestToShop, addPartToShopBasket, getShopItemInfoForArticle } = useShopConnector();

//TODO: QIT-6160 Remove temp feature flag
//temporary fresenius fix: hide controls
const { hasAccess: hideFresenius } = useHasAccess({ featureID: AccessFeature.fresenius_shop_gui });

// internal state
const quantitySelected = ref(1);
const loading = ref(true);
const infoItem: Ref<null | ShopCore.WidgetItem> = ref(null);
const priceRequestError = ref(false);
const addToBasketError = ref(false);

// compute article ID
const articleId = computed(() => {
    if (props.widgetContext.__type === "shopContext") {
        return props.widgetContext.articleId;
    } else {
        return "";
    }
});

// compute context
const context = computed(() => {
    if (props.widgetContext.__type === "shopContext") {
        return props.widgetContext.context;
    } else {
        return undefined;
    }
});

// compute flat
const flat = computed(() => {
    if (props.widgetContext.__type === "shopContext") {
        //flatView is forced view all in row, makes only sense for desktop size and is problematic for tablet and mobile
        //therefore flat is only available in desktop size
        return props.widgetContext.flatView && isLargerThanMedium.value;
    } else {
        return false;
    }
});

const moreSelectedThanAvailable = computed(() => {
    return (
        infoItem.value !== null &&
        quantitySelected.value > (infoItem.value.stock.quantity ?? 0) &&
        infoItem.value.stock.quantity !== -1
    );
});

const shopLightNotOrderable = computed(() => {
    return store.isShopLight && infoItem.value?.stock.available === ShopCore.StockStatus.UNAVAILABLE;
});

// watch for changes in shopItem and reset internal states
watch(
    () => get(infoItem),
    () => {
        set(priceRequestError, false);
        set(addToBasketError, false);
        set(quantitySelected, 1);
    }
);

// watch for changes in widgetContext
watch(
    () => props.widgetContext,
    () => {
        set(loading, true);
        getArticleInfos()
            .then((result) => {
                set(infoItem, result);
            })
            .catch(() => {
                set(infoItem, null);
            })
            .finally(() => {
                set(loading, false);
            });
    },
    { deep: true, immediate: true }
);

// quantity change handler
function quantityInputChange(val: number) {
    set(quantitySelected, val);
}

// send price request
async function sendPriceRequest() {
    if (!articleId.value) {
        return;
    }
    try {
        await sendPriceRequestToShop(articleId.value, context.value);
        // The shop decides what the request does to the cart; reload it so the
        // badge shows what the shop reports (QIT-7369).
        const { triggerBasketUpdate } = useShopEvents();
        triggerBasketUpdate();
        notify({
            message: t("shop.Request sent successfully"),
            type: "positive",
            timeout: 5_000,
        });
    } catch {
        priceRequestError.value = true;
        notify({
            message: t("shop.Sending price request failed. Please try again."),
            type: "negative",
            timeout: 5_000,
        });
        return undefined;
    }
}

// add article to basket
async function addArticleToBasket() {
    if (!articleId.value) {
        return;
    }
    try {
        await addPartToShopBasket(articleId.value, Number(quantitySelected.value), context.value);
        // Trigger basket update notification
        const { triggerBasketUpdate } = useShopEvents();
        triggerBasketUpdate();
        notify({
            message: t("shop.Article added to cart"),
            type: "positive",
            timeout: 5_000,
        });
    } catch {
        addToBasketError.value = true;
        notify({
            message: t("shop.Article could not be added to the cart. Please try again."),
            type: "negative",
            timeout: 5_000,
        });
        return undefined;
    }
}

// get article infos
async function getArticleInfos(): Promise<ShopCore.WidgetItem | null> {
    if (!articleId.value) {
        return null;
    }
    try {
        const productInfo = await getShopItemInfoForArticle(articleId.value, context.value);
        // used for debug purposes
        // if (productInfo && productInfo.price) {
        //     productInfo.price.amount = null; // Force hide price until user interaction
        //     /productInfo.price.currency = undefined; // Force hide currency until user interaction
        //     productInfo.price.currencySign = undefined; // Force hide currency sign until user interaction
        // }
        return productInfo;
    } catch {
        return null;
    }
}
</script>
<style lang="scss" scoped>
.shop-wrapper {
    border-top: 1px solid $border-color-default;
    padding: $spacing-l;
}

.shop-widget {
    color: $text-color-medium;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex-flow: space-between;
    gap: $spacing-l;
}

.widget-flat {
    flex-direction: row;
    justify-content: space-between;
}

.controls-flat {
    max-width: 400px;
}

.not-orderable {
    color: $grey-40;
    /* 
        this explicit font overright are based on figma
        the designs currently use a different font size and weight than the default for this element
        FIXME: this should be fixed in the design system
    */
    font-size: 12px;
    font-weight: 600;
}
</style>
